import type { CountryCode, Movie } from '@filmsloth/shared';
import { PROVIDER_INFO, isCommunityMovie } from '@filmsloth/shared';
import { useGenreLabel, useMovieTitle, useRating, useT } from '../i18n/index.js';
import { Poster } from './Poster.js';
import { SaveButton } from './SaveButton.js';
import { TrailerPlayer } from './TrailerPlayer.js';
import { TrailerSubmitForm } from './TrailerSubmitForm.js';

export function MovieCard({ movie, country }: { movie: Movie; country: CountryCode }) {
  const t = useT();
  const movieTitle = useMovieTitle();
  const genreLabel = useGenreLabel();
  const rating = useRating();
  const genreLabels = movie.genres.map(genreLabel).join(' · ');
  const providers = movie.availability[country] ?? [];
  const displayTitle = movieTitle(movie);
  const hasOriginalTitle = Boolean(movie.originalTitle) && movie.originalTitle !== displayTitle;

  return (
    <div className="movie-card">
      {/* A trailer says more than any cover; without one the generated cover
          takes the same slot. */}
      {movie.trailer ? (
        <TrailerPlayer movie={movie} trailer={movie.trailer} />
      ) : (
        <>
          <Poster movie={movie} />
          {/* Nobody has to fill this gap, but the person who knows the trailer
              is usually the one looking at the empty cover. */}
          <TrailerSubmitForm movie={movie} />
        </>
      )}
      <div className="movie-card__body">
        <h2>{displayTitle}</h2>
        {/* Community entries are verified, not curated. Saying so lets people
            weigh what they are looking at, the same way trailers do. */}
        {isCommunityMovie(movie.id) ? (
          <span className="badge badge--community">{t('movie.community')}</span>
        ) : null}
        {hasOriginalTitle && <div className="small muted">{movie.originalTitle}</div>}
        <div className="movie-card__meta">
          {movie.year !== null && <span>{movie.year}</span>}
          {movie.runtimeMinutes !== null && (
            <span>{t('common.minutes', { count: movie.runtimeMinutes })}</span>
          )}
          {genreLabels && <span>{genreLabels}</span>}
          {movie.rating != null && (
            <span
              title={
                movie.ratingCount != null
                  ? t('movie.ratingVotes', { count: movie.ratingCount.toLocaleString() })
                  : undefined
              }
            >
              ★ {rating(movie.rating)}
            </span>
          )}
        </div>
        <p className="movie-card__overview">{movie.overview}</p>
        <div className="row row--between">
          <SaveButton movie={movie} />
        </div>
        <div className="chips">
          {providers.map((id) => (
            <span key={id} className="chip chip--static">
              <span className="chip__dot" style={{ background: PROVIDER_INFO[id].color }} />
              {PROVIDER_INFO[id].name}
            </span>
          ))}
        </div>
        {/* Where the services above come from. JustWatch provides the data to
            TMDB on the condition that it is credited where it is shown. */}
        {providers.length > 0 ? (
          <p className="small muted movie-card__source">{t('movie.providersSource')}</p>
        ) : null}
      </div>
    </div>
  );
}
